import { StatusInformationSeverityEnum } from '@c2b/muven-commons';
import { ServiceStatusInformationModel } from './ServiceStatusInformationModel';

// Quanto maior, mais grave - define a ordem dos grupos e a cor da Central.
const SEVERITY_WEIGHT: { [status: number]: number } = {
    [StatusInformationSeverityEnum.NotWorking]: 3,
    [StatusInformationSeverityEnum.Warning]: 2,
    [StatusInformationSeverityEnum.Info]: 1,
};

export type SeverityClass = 'critical' | 'warning' | 'info';

const SEVERITY_CLASS: { [status: number]: SeverityClass } = {
    [StatusInformationSeverityEnum.NotWorking]: 'critical',
    [StatusInformationSeverityEnum.Warning]: 'warning',
    [StatusInformationSeverityEnum.Info]: 'info',
};

function normalizeDescription(description: string): string {
    return (description || '').replace(/<[^>]*>/g, '').trim().toLowerCase();
}

export class StatusInformationGroupModel {
    static keyOf(item: ServiceStatusInformationModel): string {
        return `${item.status}|${normalizeDescription(item.description)}`;
    }

    key: string;
    status: StatusInformationSeverityEnum;
    title: string;
    items: ServiceStatusInformationModel[] = [];

    constructor(item: ServiceStatusInformationModel) {
        this.key = StatusInformationGroupModel.keyOf(item);
        this.status = item.status;
        this.title = item.description;
    }

    get count(): number {
        return this.items.length;
    }

    get severityClass(): SeverityClass {
        return SEVERITY_CLASS[this.status];
    }

    get first(): ServiceStatusInformationModel {
        return this.items[0];
    }

    isSingle(): boolean {
        return this.items.length === 1;
    }
}

// Junta alertas do mesmo tipo (mesma gravidade + mesma descrição) em uma linha só.
// Ex.: um "Canal inativo." por canal vira um grupo com N itens.
export function groupStatusInformation(list?: ServiceStatusInformationModel[]): StatusInformationGroupModel[] {
    const groups = new Map<string, StatusInformationGroupModel>();

    for (const item of list || []) {
        if (!SEVERITY_WEIGHT[item.status]) {
            continue;
        }

        const key = StatusInformationGroupModel.keyOf(item);
        let group = groups.get(key);

        if (!group) {
            group = new StatusInformationGroupModel(item);
            groups.set(key, group);
        }

        group.items.push(item);
    }

    return Array.from(groups.values()).sort((a, b) => {
        return (SEVERITY_WEIGHT[b.status] - SEVERITY_WEIGHT[a.status]) || (b.count - a.count);
    });
}

export function worstSeverityClass(groups: StatusInformationGroupModel[]): SeverityClass | 'ok' {
    return groups.length ? groups[0].severityClass : 'ok';
}

export function countBySeverity(groups: StatusInformationGroupModel[]): { [severity in SeverityClass]: number } {
    const result = { critical: 0, warning: 0, info: 0 };

    for (const group of groups) {
        result[group.severityClass]++;
    }

    return result;
}
